
import { ACTION_TYPES } from 'utils/constants/app_router';

function getWindow() {
    if (typeof window !== 'undefined') {
        return window;
    }
    return undefined;
}

/**
 * Sync vendor_id from actionData to localStorage (vendor_id === outlet_id)
 * Call this before any navigation to ensure the correct outlet context
 * @param {object} actionData - The payload data containing vendor_id
 */
function syncVendorIdToLocalStorage(actionData) {
    const vendorId = actionData?.seller_outlet_id ||
        actionData?.sellerOutletId ||
        actionData?.SellerOutletId ||
        actionData?.vendor_id ||
        actionData?.vendorId ||
        actionData?.VendorId;
    if (vendorId && getWindow()) {
        window.localStorage.setItem('outlet_id', vendorId);
    }
}

function getActionHandler(actionType, otherData = {}) {
    const handlers = otherData?.actionHandlers || {};
    return handlers[actionType] || handlers[actionType?.toLowerCase?.()] || null;
}

function executeOverrideHandler(actionType, actionData, navigate, otherData) {
    const handler = getActionHandler(actionType, otherData);
    if (!handler) return false;

    handler(actionData, navigate, otherData);
    return true;
}

function getPurchaseOrderId(actionData = {}) {
    return actionData.purchase_order_id || actionData.purchaseOrderId || actionData.PurchaseOrderId;
}

function getPoGrnMappingId(actionData = {}) {
    return actionData.po_grn_mapping_id || actionData.poGrnMappingId || actionData.PoGrnMappingId || actionData.grn_id || actionData.grnId || actionData.GrnId;
}

function normalizeSellerRoute(route) {
    if (!route || typeof route !== 'string') {
        return '';
    }

    const trimmedRoute = route.trim();
    if (!trimmedRoute) {
        return '';
    }

    if (/^https?:\/\//i.test(trimmedRoute)) {
        return trimmedRoute;
    }

    const sellerSchemeIndex = trimmedRoute.indexOf('://');
    if (sellerSchemeIndex !== -1) {
        const withoutScheme = trimmedRoute.slice(sellerSchemeIndex + 3);
        const pathStart = withoutScheme.indexOf('/');
        return pathStart === -1 ? '/' : withoutScheme.slice(pathStart);
    }

    return trimmedRoute.startsWith('/') ? trimmedRoute : `/${trimmedRoute}`;
}

function getNavigationRoute(actionData) {
    return actionData?.route ||
        actionData?.Route ||
        actionData?.path ||
        actionData?.Path ||
        actionData?.url ||
        actionData?.URL ||
        actionData?.deeplink ||
        actionData?.DeepLink ||
        actionData?.Deeplink ||
        '';
}

function handleNavigation(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);

    const route = normalizeSellerRoute(getNavigationRoute(actionData));
    if (!route) {
        console.warn('[handleNavigation] Missing route/path/url/deeplink');
        return false;
    }

    if (/^https?:\/\//i.test(route)) {
        getWindow()?.location.assign(route);
        return true;
    }

    navigate(route);
    return true;
}

function handlePoPendingAcceptanceDetail(actionData, navigate, otherData) {
    const purchaseOrderId = getPurchaseOrderId(actionData);
    if (!purchaseOrderId) {
        console.warn('[handlePoPendingAcceptanceDetail] Missing purchaseOrderId');
        return false;
    }
    syncVendorIdToLocalStorage(actionData);
    navigate(`/po/details/pending/${purchaseOrderId}`);
    return true;
}

function handleDdPoListPage(actionData, navigate, otherData) {
    const purchaseOrderId = getPurchaseOrderId(actionData);
    syncVendorIdToLocalStorage(actionData);
    if (purchaseOrderId) {
        navigate(`/ddPo/details/invoice/${purchaseOrderId}`);
    } else {
        navigate('/ddPo/all');
    }
    return true;
}

function handleAllPoNoGrnYetPage(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);
    // Navigate to /po/all with tab=2 (No GRN Yet tab)
    navigate('/po/all?tab=2');
    return true;
}

function handleGrnPendingConfirmationPage(actionData, navigate, otherData) {
    const purchaseOrderId = getPurchaseOrderId(actionData);
    const poGrnMappingId = getPoGrnMappingId(actionData);

    syncVendorIdToLocalStorage(actionData);

    if (purchaseOrderId && poGrnMappingId) {
        navigate(`/grn/details/list/${purchaseOrderId}/${poGrnMappingId}`);
    } else {
        navigate('/grn/list');
    }
    return true;
}

function handleSellerPoList(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);
    navigate('/po/all');
    return true;
}

function handleSellerDdPoList(actionData, navigate, otherData) {
    return handleDdPoListPage(actionData, navigate, otherData);
}

function handleSellerBiddings(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);
    navigate('/bids/all');
    return true;
}

function handleSellerGrnPending(actionData, navigate, otherData) {
    return handleGrnPendingConfirmationPage(actionData, navigate, otherData);
}

function handleSellerPoDetail(actionData, navigate, otherData) {
    return handlePoPendingAcceptanceDetail(actionData, navigate, otherData);
}

function handleSellerDdPoDetail(actionData, navigate, otherData) {
    const purchaseOrderId = getPurchaseOrderId(actionData);
    const source = actionData?.source || actionData?.Source || 'invoice';
    if (!purchaseOrderId) {
        return handleSellerDdPoList(actionData, navigate, otherData);
    }

    syncVendorIdToLocalStorage(actionData);
    navigate(`/ddPo/details/${source}/${purchaseOrderId}`);
    return true;
}

function handleSellerGrnDetail(actionData, navigate, otherData) {
    return handleGrnPendingConfirmationPage(actionData, navigate, otherData);
}

function handleSellerPayouts(actionData, navigate, otherData) {
    syncVendorIdToLocalStorage(actionData);
    navigate('/payouts');
    return true;
}


/**
 * Handles action type based navigation/effects - used by JSBridge for native app communication
 * @param {string} actionType - The action type string from backend/native
 * @param {object} actionData - The payload data for the action
 * @param {function} navigate - react-router navigate function
 * @param {object} otherData - Additional handlers (modals, toasts, stores, etc.)
 * @returns {boolean} - true if action was handled, false otherwise
 */
export function handleActionType(actionType, actionData = {}, navigate, otherData = {}) {
    if (!actionType) {
        console.warn('[handleActionType] No actionType provided');
        return false;
    }

    const normalizedActionType = typeof actionType === 'string' ? actionType.toUpperCase() : actionType;

    if (executeOverrideHandler(normalizedActionType, actionData, navigate, otherData)) {
        return true;
    }

    switch (normalizedActionType) {
        case ACTION_TYPES.NAVIGATION:
        case ACTION_TYPES.ROUTE:
        case ACTION_TYPES.DEEPLINK:
            return handleNavigation(actionData, navigate, otherData);

        case ACTION_TYPES.PO_PENDING_ACCEPTANCE_DETAIL:
            return handlePoPendingAcceptanceDetail(actionData, navigate, otherData);

        case ACTION_TYPES.DD_PO_LIST_PAGE:
            return handleDdPoListPage(actionData, navigate, otherData);

        case ACTION_TYPES.ALL_PO_NO_GRN_YET_PAGE:
            return handleAllPoNoGrnYetPage(actionData, navigate, otherData);

        case ACTION_TYPES.GRN_PENDING_CONFIRMATION_PAGE:
            return handleGrnPendingConfirmationPage(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_PO_LIST:
            return handleSellerPoList(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_DD_PO_LIST:
            return handleSellerDdPoList(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_GRN_PENDING:
            return handleSellerGrnPending(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_PO_DETAIL:
            return handleSellerPoDetail(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_DD_PO_DETAIL:
            return handleSellerDdPoDetail(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_GRN_DETAIL:
            return handleSellerGrnDetail(actionData, navigate, otherData);

        case ACTION_TYPES.SELLER_PAYOUTS:
            return handleSellerPayouts(actionData, navigate, otherData);

        default:
            console.warn(`[handleActionType] Unknown actionType: ${actionType}`);
            if (navigate) {
                navigate('/');
            }
            return false;
    }
}

/**
 * Parse action payload from JSBridge (handles string, object, various casings)
 * @param {string|object} payload - The payload from JSBridge
 * @returns {object} Parsed payload with actionType and actionData
 */
export function parseActionPayload(payload) {
    if (!payload) return { actionType: null, actionData: {} };

    try {
        const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
        return {
            actionType: parsed.actionType || parsed.ActionType || parsed.action_type,
            actionData: parsed.actionData || parsed.ActionData || parsed.action_data || {},
        };
    } catch (e) {
        console.error('[parseActionPayload] Failed to parse payload:', e);
        return { actionType: null, actionData: {} };
    }
}

/**
 * Setup JSBridge handler for action-based navigation
 * Call once at app init with navigate and any additional handlers
 * @param {function} navigate - react-router navigate function
 * @param {object} otherData - Additional handlers (showModal, showToast, etc.)
 */
export function setupJSBridgeActionHandler(navigate, otherData = {}) {
    if (!getWindow()) return;

    window.handleActionTypeFromNative = (payload) => {
        const { actionType, actionData } = parseActionPayload(payload);
        return handleActionType(actionType, actionData, navigate, otherData);
    };

    window.handleSellerHubAction = window.handleActionTypeFromNative;
}
